// @ts-nocheck
import React from "react";
import useOpenDocument from "./useOpenDocument";
import CreateReportModal from "../components/modals/client-report-create";
import EditReportModal from "../features/clientreportseditmodal";
import { useAuth } from "react-oidc-context";
import { toNString } from "../helpers/guids";
import { ConfirmModalMessageText, openConfirmModal } from "../components/modals/ConfirmModal";
import { openClientReportHistoryModal } from "../components/modals/client-report-history";
import { openCreateClientReportModal } from "../components/modals/client-report-create";
import { ClientReportControlsProvider } from "./ClientReportControlsContext";
import { useLazyFetchMasterAccountReportsQuery, usePatchReportMutation, usePatchReportStatusMutation } from "../services/clientreports";
import { ClientReportType } from "../types/ClientReportType";
import { PublishClientReportRequestType } from "../services/clientreports/types/PublishClientReportRequestType";
import { ClientReportCreateModalOptions } from "../components/modals/client-report-create/types";

export type ClientReportContextProps = {
    clear: () => void,
    data: ClientReportType[],
    error: Error | string | null,
    getStatusDescription: (v: ClientReportType) => string,
    hasMore: boolean,
    isFetching: boolean,
    isLoading: boolean,
    isNextPageLoading: boolean,
    isNewRecords: boolean,
    isRowLoaded: boolean,
    limit: number,
    loadMore: () => void,
    newRecordsSinceCache: number,
    openReport: (documentId: string, fileName?: string | null) => void,
    page: number,
    pagination: Pagination,
    setPageLimit: (v: number) => void,
    setSearchTerm: (v: string) => void,
    sort: string,
    sortTable: (property: string) => void,
    totalCount: number,
    totalPages: number 
};

export type ClientReportProviderProps = {
    adviserId: string,
    children: JSX.Element,
    masterAccountId?: string | null,
    divisionId?: string | null,
    projectId?: string | null,
    filter?: any | null,
    isReady?: boolean,
    query?: any
};

const ClientReportContext = React.createContext<ClientReportContextProps | null>(null);

export const useClientReportsList = () => React.useContext(ClientReportContext);

// NOTE: passing query in so that it can be overriden for the detailed/management views

export const ClientReportProvider = ({ 
    adviserId,
    children, 
    divisionId = null, 
    filter = null,
    isReady = true,
    masterAccountId = null, 
    projectId = null,
    query = useLazyFetchMasterAccountReportsQuery
} : Readonly<ClientReportProviderProps>) : JSX.Element => {
    // setup state and our query state (does not load by default (lazy))
    const auth = useAuth();
    const { sub } = auth.user!.profile;
    const [isEditModalOpen, setIsEditModalOpen] = React.useState<boolean>(false);
    const [sort, setSort] = React.useState("CreatedDate+desc");
    const [pageLimit, setPageLimit] = React.useState(50);
    const [searchTerm, setSearchTerm] = React.useState("");
    const [selectedReport, _setReport] = React.useState(null);
    const [isClearing, setIsClearing] = React.useState(false);
    const [cacheKey, setCacheKey] = React.useState(new Date().valueOf());
    const [isLoadingState, setIsLoadingState] = React.useState(false);
    const [load, { data, isLoading: isLoadingData, isFetching, ...loadResults }] = query();
    const [update, { isLoading: isPatching, ...patchResults }] = usePatchReportMutation();
    const [updateStatus, { isLoading: isPatchingStatus, ...patchStatusResults }] = usePatchReportStatusMutation();
    const { openDocument, isFetching: isOpening } = useOpenDocument();

    // extract the results of the last load to our report search
    // note: we need to have a default set of data in-case we have an undefined response
    const { pagination, results, newRecordsSinceCache } = data || { pagination: { page: 1, pageSize: pageLimit, totalCount: 0, totalPages: 1 }, results: [], newRecordsSinceCache: 0 };
    const { page, totalCount, totalPages } = pagination;
    const hasMore = totalCount > 0 && page < totalPages;
    const isLoading = isLoadingData === true || isPatching === true || isPatchingStatus === true;
    const isNextPageLoading = isFetching === true || isLoadingData === true || isLoadingState === true;

    const buildOptions = (cacheKey, page) => {
        return filter != null
            ? {
                ...filter,
                cacheKey: cacheKey,
                sort,
                page,
                limit: pageLimit
            }
            : {
                cacheKey: cacheKey,
                masterAccountId,
                projectId,
                searchText: searchTerm,
                sort,
                page,
                limit: pageLimit
            }
    };

    const clear = () => {
        if ((!masterAccountId && !filter) || isNextPageLoading || !isReady) {
            return;
        }

        const newCacheKey = new Date().valueOf();
        const queryOptions = buildOptions(newCacheKey, 1);

        setIsLoadingState(_ => true);
        setIsClearing(_ => true);
        load(queryOptions).unwrap().then(_ => {
            setCacheKey(_prev => newCacheKey);
        }).finally(_ => {
            setIsLoadingState(_prev => false);
            setIsClearing(_ => false);
        });
    };

    const closeEditModal = () => {
        setIsEditModalOpen(_ => false);
        _setReport(_ => null);
    };

    const getStatusDescription = (v: ClientReportType) : string => {
        const { status, statusDescription, sentByMediaType } = v;
        if (status !== 17) {
            return statusDescription;
        }
        switch(sentByMediaType) {
            case 0:
                return "Sent via Email";
            case 1:
                return "Sent via Post";
            case 2:
                return "Sent via Online Dashboard"
            default:
                return statusDescription;
        }
    };

    const isRowLoaded = (index) => {
        return !hasMore || index < results.length;
    };

    const loadMore = () => {
        if (isNextPageLoading || !hasMore || !isReady) {
            return;
        }

        setIsLoadingState(_ => true);
        load(buildOptions(cacheKey, (page + 1))).unwrap().then(_ => {
            setIsLoadingState(_prev => false);
        });
    };

    const patchReport = (operations, message = null, targetReport = null) => {
        const report = targetReport ?? selectedReport;
        if (!report) {
            return new Promise((_, reject) => reject(`A report has not been selected.`));
        }

        const { id, masterAccountId, projectId } = report;
        return new Promise((resolve, reject) => {
            // Patch status separate if we have a custom message
            if (message == null) {
                return update({ masterAccountId, reportId: id, projectId, operations }).unwrap().then(
                    r => resolve(r),
                    e => reject(e)
                );
            }

            const { authorId, status, message: { threadId, text, recipients } } = message;

            // Patch non-status properties first, if there are any
            const statusPatch = () => updateStatus({
                masterAccountId,
                reportId: id,
                projectId,
                authorId,
                status,
                messageThread: threadId,
                messageText: text,
                messageRecipients: recipients
            }).unwrap();
            
            return (
                operations.length > 0
                    ? update({ masterAccountId, reportId: id, projectId, operations }).unwrap().then(statusPatch)
                    : statusPatch()
            ).then(resolve, reject);
        });
    };

    const publishReport = (formData: PublishClientReportRequestType, sendType: number) => new Promise((resolve, reject) => {
        resolve(formData);
    });

    const showCreateModal = () => {
        const defaultAuthorId = null; // toNString(sub);
        openCreateClientReportModal({ 
            adviserId,
            authorId: defaultAuthorId,
            divisionId,
            masterAccountId,
            projectId,
            reviewId: null
        } as ClientReportCreateModalOptions)
        .then(
            (r: ClientReportType) => openConfirmModal({
                confirmButtonText: 'Open Report',
                confirmButtonVariant: 'primary',
                dialogTitle: `Open '${r.reportName}'?`,
                message: (<ConfirmModalMessageText>Would you like to open the newly created report?</ConfirmModalMessageText>)
            })
            .then((shouldOpen: boolean) => {
                if (shouldOpen === true) {
                    openDocument(r.documentId, r.reportName);
                }
            })
            .finally(() => clear())
        );
    }

    const showHistoryModal = (report: ClientReportType) => {
        openClientReportHistoryModal(report.masterAccountId, report.id).then();
    };

    const showEditModal = (report) => {
        if (!report) {
            return;
        }

        // TODO: we could add a bit that checks if the ID is passed, then get and set the data
        // otherwise just set the state and open the modal
        _setReport(_ => report);
        setIsEditModalOpen(_ => true);
    };

    const showSendModal = (report: ClientReportType, sendType: number | null = null) => { };

    const sortTable = (property) => {
        // we want the sort to initially be descending ??
        let newDirection = "";
        let [sortProperty, direction] = sort.split("+");

        // if the current property is already selected, reverse the sort direction
        if (property === sortProperty) {
            newDirection = direction === "DESC" ? null : "+DESC";
        }

        setSort(property + (newDirection ?? ""));
        clear();
    };

    React.useEffect(clear, [ searchTerm, sort, masterAccountId, projectId, filter, isReady ]);

    return (
        <ClientReportContext.Provider value={{
            ...loadResults,
            clear,
            closeEditModal,
            data: isClearing ? [] : results,
            getStatusDescription,
            hasMore,
            isFetching,
            isLoading,
            isNextPageLoading,
            isNewRecords: newRecordsSinceCache > 0,
            isRowLoaded,
            limit: pageLimit,
            loadMore: isNextPageLoading ? () => {} : loadMore, 
            newRecordsSinceCache,
            openReport: isOpening ? () => {} : openDocument,
            page,
            pagination,
            patchReport,
            publishReport,
            searchTerm,
            selectedReport,
            setSearchTerm,
            showCreateModal,
            showHistoryModal,
            showEditModal,
            showSendModal,
            sort,
            sortTable,
            totalCount,
            totalPages
        }}>
            <ClientReportControlsProvider>
                {children}
            </ClientReportControlsProvider>
        </ClientReportContext.Provider>
    );
};